import {
  MixedSchema,
  isMixedSchema,
  isRawInlineNodes,
  type RawInlineNodes,
} from '@principle-theorem/editor';
import {
  TypeGuard,
  isDocRef,
  isEnumValue,
  isINamedDocument,
  isObject,
  type ArchivedDocument,
  type DocumentReference,
  type INamedDocument,
  type ISoftDelete,
  type Timestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { isArray, isBoolean, isString } from 'lodash';
import { type Observable } from 'rxjs';
import { ISchedulingEvent } from '../appointment/cancellation';
import { PrincipleCreatedBy } from '../common';
import { type IMedia } from '../media/media';
import { type IPrincipleMention } from '../mention/mention';
import { type IPractice } from '../practice/practice';
import { type IStaffer } from '../staffer/staffer';
import { type ITag } from '../tag/tag';
import { type ICallEvent } from './call-event';

export enum InteractionCollection {
  History = 'interactionHistory',
}

export enum InteractionType {
  Appointment = 'appointment',
  AppointmentArrived = 'appointment.arrived',
  AppointmentBook = 'appointment.book',
  AppointmentCancel = 'appointment.cancel',
  AppointmentCheckIn = 'appointment.checked-in',
  AppointmentCheckOut = 'appointment.checked-out',
  AppointmentComplete = 'appointment.complete',
  AppointmentConfirm = 'appointment.confirm',
  AppointmentFollowUp = 'appointment.follow-up',
  AppointmentOffer = 'appointment.offer',
  AppointmentReminder = 'appointment.reminder',
  AppointmentReschedule = 'appointment.reschedule',
  AppointmentStart = 'appointment.start',
  Call = 'call',
  CallEvent = 'call-event',
  CallReceived = 'call.recieved',
  ClinicalNote = 'clinical-note',
  Email = 'email',
  Form = 'form',
  FormConfirm = 'form.confirm',
  FormIssue = 'form.issue',
  FormReissue = 'form.reissue',
  FormSubmit = 'form.submit',
  Gap = 'gap',
  Invoice = 'invoice',
  LabJob = 'lab-job',
  LabJobIssued = 'lab-job.issued',
  Note = 'note',
  PartnerIntegration = 'partner-integration',
  Patient = 'patient',
  Payment = 'payment',
  ReferralCreated = 'referral.created',
  ReferralChangesNotified = 'referral.changes-notified',
  ReferralEmailSent = 'referral.email-sent',
  ReferralStatusChanged = 'referral.status-changed',
  ReferralReadBy = 'referral.read-by',
  Sms = 'sms',
  SmsReceived = 'sms.recieved',
  Task = 'task',
  TaskAssign = 'task.assign',
  TaskComplete = 'task.complete',
  TaskCreate = 'task.create',
  TaskDelete = 'task.delete',
  TaskEdit = 'task.edit',
  TaskEditDue = 'task.edit.due',
  TaskReopen = 'task.reopen',
  TaskUndelete = 'task.undelete',
  Webhook = 'webhook',

  // Conversion Types
  OnlineBookingPageViewed = 'appointment.online-booking-page-viewed',
  OnlineBooking = 'appointment.booked-online',
  EmailOpened = 'email.opened',
  InvoicePaid = 'invoice.paid',
  LinkClicked = 'link.clicked',
}

export const INTERACTION_TYPE_LABEL_MAP: { [key in InteractionType]: string } =
  {
    [InteractionType.Patient]: 'Patient',
    [InteractionType.Appointment]: 'Appointment',
    [InteractionType.Invoice]: 'Invoice',
    [InteractionType.Gap]: 'Gap',
    [InteractionType.Payment]: 'Payment',
    [InteractionType.Email]: 'Email',
    [InteractionType.Sms]: 'SMS',
    [InteractionType.Call]: 'Call',
    [InteractionType.CallEvent]: 'AI Receptionist',
    [InteractionType.Task]: 'Task',
    [InteractionType.Note]: 'Note',
    [InteractionType.LabJob]: 'Lab Job',
    [InteractionType.LabJobIssued]: 'Lab Job Issued',
    [InteractionType.SmsReceived]: 'SMS Recieved',
    [InteractionType.CallReceived]: 'Call Recieved',
    [InteractionType.TaskCreate]: 'Task Create',
    [InteractionType.TaskAssign]: 'Task Assign',
    [InteractionType.TaskComplete]: 'Task Complete',
    [InteractionType.TaskReopen]: 'Task Reopen',
    [InteractionType.TaskDelete]: 'Task Delete',
    [InteractionType.TaskUndelete]: 'Task Undelete',
    [InteractionType.TaskEdit]: 'Task Edit',
    [InteractionType.TaskEditDue]: 'Task Edit Due',
    [InteractionType.AppointmentBook]: 'Appointment Book',
    [InteractionType.AppointmentOffer]: 'Appointment Offer',
    [InteractionType.AppointmentStart]: 'Appointment Start',
    [InteractionType.AppointmentCancel]: 'Appointment Cancel',
    [InteractionType.AppointmentConfirm]: 'Appointment Confirm',
    [InteractionType.AppointmentReminder]: 'Appointment Reminder',
    [InteractionType.AppointmentComplete]: 'Appointment Complete',
    [InteractionType.AppointmentArrived]: 'Appointment Arrived',
    [InteractionType.AppointmentCheckIn]: 'Appointment Checked In',
    [InteractionType.AppointmentCheckOut]: 'Appointment Checked Out',
    [InteractionType.AppointmentReschedule]: 'Appointment Reschedule',
    [InteractionType.AppointmentFollowUp]: 'Appointment Follow Up',
    [InteractionType.ClinicalNote]: 'Clinical Note',
    [InteractionType.Form]: 'Form',
    [InteractionType.FormSubmit]: 'Form Submitted',
    [InteractionType.FormConfirm]: 'Form Confirmed',
    [InteractionType.FormIssue]: 'Form Issued',
    [InteractionType.FormReissue]: 'Form Reissued',
    [InteractionType.PartnerIntegration]: 'Partner Integration',
    [InteractionType.Webhook]: 'Webhook',
    [InteractionType.ReferralCreated]: 'Referral Created',
    [InteractionType.ReferralEmailSent]: 'Referral Email Sent',
    [InteractionType.ReferralChangesNotified]: 'Referral Changes Notified',
    [InteractionType.ReferralStatusChanged]: 'Referral Status Changed',
    [InteractionType.ReferralReadBy]: 'Referral Read By',

    // Conversion Types
    [InteractionType.OnlineBookingPageViewed]: 'Online Booking Page Viewed',
    [InteractionType.OnlineBooking]: 'Appointment Booked Online',
    [InteractionType.EmailOpened]: 'Email Opened',
    [InteractionType.InvoicePaid]: 'Invoice Paid',
    [InteractionType.LinkClicked]: 'Email Link Clicked',
  };

/**
 * Suggested customer-facing source labels for API keys; extend as recognised
 * producers grow. Single source of truth shared by the sourceLabel editor
 * autocomplete and any programmatic key setup.
 */
export const KNOWN_CALL_EVENT_SOURCE_LABELS: string[] = [
  INTERACTION_TYPE_LABEL_MAP[InteractionType.CallEvent],
];

export type WithContext<T> = T & {
  suffix?: RawInlineNodes;
  originalType?: InteractionType;
  callEvent?: WithRef<ICallEvent>;
};

export interface IEmailAttachment {
  name: string;
  type: string;
  size: number;
  mediaRef?: DocumentReference<IMedia>;
  patientMediaRef?: DocumentReference<IMedia>;
  savedToPatientFiles?: boolean;
  emailRef?: DocumentReference;
}

export interface IInteraction {
  uid: string;
  type: InteractionType;
  owner?: INamedDocument<IStaffer> | PrincipleCreatedBy;
  title: RawInlineNodes;
  action?: string;
  createdAt: Timestamp;
  content: MixedSchema;
}

export interface IInteractionV2 extends IInteraction, ISoftDelete {
  pinned: boolean;
  amendmentOf?: DocumentReference<ArchivedDocument<IInteractionV2>>;
  tags: INamedDocument<ITag>[];
  schedulingEvent?: DocumentReference<ISchedulingEvent>;
  attachments?: IEmailAttachment[];
  /**
   * Deliberately optional: interaction documents have never carried a practice,
   * so every historical document and every writer that cannot resolve one must
   * keep round-tripping without it.
   */
  practiceRef?: DocumentReference<IPractice>;
}

export interface IPinnedInteractionSummary {
  uid: string;
  createdAt: Timestamp;
  createdBy?: PrincipleCreatedBy;
  content: MixedSchema;
}

export interface IInteractable {
  interactions: IInteraction[];
}

export function isInteractable(data: unknown): data is IInteractable {
  return isObject(data) && 'interactions' in data && isArray(data.interactions);
}

export function isInteraction(data: unknown): data is IInteraction {
  return (
    isObject(data) &&
    isString(data.uid) &&
    isEnumValue(InteractionType, data.type) &&
    (isObject(data.title) || isArray(data.title)) &&
    isRawInlineNodes(data.title) &&
    isMixedSchema(data.content)
  );
}

export function isInteractionV2(data: unknown): data is IInteractionV2 {
  return (
    isObject(data) &&
    isInteraction(data) &&
    isBoolean(data.pinned) &&
    isBoolean(data.deleted) &&
    TypeGuard.arrayOf(isINamedDocument)(data.tags) &&
    TypeGuard.undefinedOr(isDocRef)(data.practiceRef)
  );
}

export interface IInteractiveResource {
  interactions$: Observable<IInteraction[]>;
  add(interaction: IInteractionV2): Promise<void>;
}

export interface IInteractionData {
  contact: IPrincipleMention;
  resource?: IInteractiveResource;
}

export const INTERACTION_TYPES: InteractionType[] = [
  InteractionType.Patient,
  InteractionType.Appointment,
  InteractionType.Gap,
  InteractionType.Invoice,
  InteractionType.Payment,
  InteractionType.Email,
  InteractionType.Sms,
  InteractionType.Call,
  InteractionType.CallEvent,
  InteractionType.Task,
  InteractionType.LabJob,
  InteractionType.Note,
  InteractionType.ClinicalNote,
  InteractionType.Form,
];

export type IInteractionTypeMap = Record<InteractionType, string>;

export const interactionTypeDisplayMap: IInteractionTypeMap = {
  [InteractionType.Patient]: 'person',
  [InteractionType.Appointment]: 'airline_seat_flat_angled',
  [InteractionType.AppointmentFollowUp]: 'airline_seat_flat_angled',
  [InteractionType.Invoice]: 'receipt',
  [InteractionType.Gap]: 'crop_free',
  [InteractionType.Payment]: 'payment',
  [InteractionType.Email]: 'email',
  [InteractionType.Sms]: 'sms',
  [InteractionType.SmsReceived]: 'question_answer',
  [InteractionType.Call]: 'phone',
  [InteractionType.CallEvent]: 'support_agent',
  [InteractionType.Task]: 'check_box',
  [InteractionType.Note]: 'text_snippet',
  [InteractionType.LabJob]: 'local_pharmacy',
  [InteractionType.LabJobIssued]: 'local_pharmacy',
  [InteractionType.TaskCreate]: 'add_box',
  [InteractionType.TaskAssign]: 'person_add',
  [InteractionType.TaskComplete]: 'done',
  [InteractionType.TaskReopen]: 'undo',
  [InteractionType.TaskDelete]: 'delete',
  [InteractionType.TaskUndelete]: 'undo',
  [InteractionType.CallReceived]: 'call_received',
  [InteractionType.AppointmentBook]: 'schedule',
  [InteractionType.AppointmentOffer]: 'send',
  [InteractionType.AppointmentStart]: 'airline_seat_flat_angled',
  [InteractionType.AppointmentReminder]: 'send',
  [InteractionType.AppointmentCancel]: 'cancel',
  [InteractionType.AppointmentConfirm]: 'done',
  [InteractionType.AppointmentComplete]: 'done',
  [InteractionType.AppointmentArrived]: 'flight_land',
  [InteractionType.AppointmentCheckIn]: 'airline_seat_recline_normal',
  [InteractionType.AppointmentCheckOut]: 'shopping_cart',
  [InteractionType.AppointmentReschedule]: 'schedule',
  [InteractionType.TaskEdit]: 'edit',
  [InteractionType.TaskEditDue]: 'timer',
  [InteractionType.ClinicalNote]: 'healing',
  [InteractionType.Form]: 'list_alt',
  [InteractionType.FormSubmit]: 'list_alt',
  [InteractionType.FormConfirm]: 'list_alt',
  [InteractionType.FormIssue]: 'list_alt',
  [InteractionType.FormReissue]: 'list_alt',
  [InteractionType.PartnerIntegration]: 'extension',
  [InteractionType.Webhook]: 'webhook',
  [InteractionType.ReferralCreated]: 'add_circle',
  [InteractionType.ReferralChangesNotified]: 'notifications',
  [InteractionType.ReferralEmailSent]: 'email',
  [InteractionType.ReferralStatusChanged]: 'update',
  [InteractionType.ReferralReadBy]: 'visibility',

  // Conversion Types
  [InteractionType.OnlineBookingPageViewed]: 'language',
  [InteractionType.OnlineBooking]: 'language',
  [InteractionType.EmailOpened]: 'drafts',
  [InteractionType.InvoicePaid]: 'paid',
  [InteractionType.LinkClicked]: 'link',
};
